import { skipToken } from "@reduxjs/toolkit/query";
import type {
  ConfigurationTestResult,
  ConfigurationTestResultId,
  ConfigurationTestResultResourceType,
} from "@repo/app-types/entities/configurationTestResult";
import { configurationTestResultIdSchema } from "@repo/app-types/entities/configurationTestResult";
import type {
  CopilotProposal,
  CopilotProposalId,
  CopilotProposalResourceType,
} from "@repo/app-types/entities/copilotProposal";
import { copilotProposalIdSchema } from "@repo/app-types/entities/copilotProposal";
import { useCallback, useEffect, useEffectEvent } from "react";
import { useSearchParams, type To } from "react-router";
import { useGetConfigurationTestResultQuery } from "../../api/entities/configurationTestResult.js";
import { useGetPendingProposalQuery } from "../../api/entities/copilotProposal.js";

/**
 * One-shot deep-link params an Ask Alpharun row sets on an editor's URL: the
 * copilot proposal to show, or the saved test result to show. The editor acts
 * on the param once it loads, then strips it — unlike a selection param it is
 * not a durable part of the URL state.
 */
export const COPILOT_PROPOSAL_QUERY_PARAM = "proposalId";
export const COPILOT_TEST_RESULT_QUERY_PARAM = "testResultId";

/** An editor's location with the proposal to show. */
export function buildCopilotProposalLocation(
  pathname: string,
  proposalId: CopilotProposalId,
) {
  const searchParams = new URLSearchParams({
    [COPILOT_PROPOSAL_QUERY_PARAM]: proposalId,
  });
  return { pathname, search: `?${searchParams.toString()}` } satisfies To;
}

/** An editor's location with the test result to show. */
export function buildCopilotTestResultLocation(
  pathname: string,
  testResultId: ConfigurationTestResultId,
) {
  const searchParams = new URLSearchParams({
    [COPILOT_TEST_RESULT_QUERY_PARAM]: testResultId,
  });
  return { pathname, search: `?${searchParams.toString()}` } satisfies To;
}

export interface CopilotDeepLink<T> {
  /** The id in the URL, until the editor clears it. */
  id: string | undefined;
  /** The row, once loaded. Undefined while loading, and after the param is cleared. */
  row: T | undefined;
  /** Set when the row is gone: resolved, deleted, or of another type. */
  isUnavailable: boolean;
  /** Strips the param from the URL. */
  clear: () => void;
}

export interface CopilotDeepLinkOptions {
  /**
   * Called once the row turns out to be unavailable, after the param is
   * cleared. Without it the param stays, for the editor to read
   * `isUnavailable`.
   */
  onUnavailable?: () => void;
}

/**
 * The pending proposal an Ask Alpharun row deep-linked this editor to with
 * `?proposalId=`. The editor puts the row into its draft when it loads and
 * clears the param. The server serves only a pending row of the named
 * resource type, so the row is typed to it.
 */
export function useDeepLinkedProposal<T extends CopilotProposalResourceType>(
  resourceType: T,
  options: CopilotDeepLinkOptions = {},
): CopilotDeepLink<CopilotProposal<T>> {
  const [searchParams] = useSearchParams();
  const proposalId = copilotProposalIdSchema.safeParse(
    searchParams.get(COPILOT_PROPOSAL_QUERY_PARAM),
  ).data;
  const query = useGetPendingProposalQuery(
    proposalId ? { resourceType, proposalId } : skipToken,
  );
  return useDeepLink(
    COPILOT_PROPOSAL_QUERY_PARAM,
    proposalId,
    query,
    options,
  ) as CopilotDeepLink<CopilotProposal<T>>;
}

/** The `?testResultId=` counterpart of `useDeepLinkedProposal`. */
export function useDeepLinkedTestResult<
  T extends ConfigurationTestResultResourceType,
>(
  resourceType: T,
  options: CopilotDeepLinkOptions = {},
): CopilotDeepLink<ConfigurationTestResult<T>> {
  const [searchParams] = useSearchParams();
  const testResultId = configurationTestResultIdSchema.safeParse(
    searchParams.get(COPILOT_TEST_RESULT_QUERY_PARAM),
  ).data;
  const query = useGetConfigurationTestResultQuery(
    testResultId ? { resourceType, testResultId } : skipToken,
  );
  return useDeepLink(
    COPILOT_TEST_RESULT_QUERY_PARAM,
    testResultId,
    query,
    options,
  ) as CopilotDeepLink<ConfigurationTestResult<T>>;
}

function useDeepLink<T extends { id: string }>(
  param: string,
  id: string | undefined,
  query: { currentData?: T; isError: boolean },
  options: CopilotDeepLinkOptions,
): CopilotDeepLink<T> {
  const [, setSearchParams] = useSearchParams();
  const clear = useCallback(
    () =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(param);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams, param],
  );

  const isUnavailable = id !== undefined && query.isError;
  const handleUnavailable = useEffectEvent(() => {
    if (options.onUnavailable) {
      clear();
      options.onUnavailable();
    }
  });
  useEffect(() => {
    if (isUnavailable) {
      handleUnavailable();
    }
  }, [isUnavailable]);

  return {
    id,
    row: id && query.currentData?.id === id ? query.currentData : undefined,
    isUnavailable,
    clear,
  };
}
